import { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import {
  LayoutDashboard, Users, Package, Gavel, Truck, Dna, Shield, CreditCard,
  Bot, AlertTriangle, Activity, Settings, LogOut, Menu, X, ChevronRight,
  Bell, MessageSquare, Eye, LifeBuoy, FolderOpen,
} from 'lucide-react';

const adminNav = [
  { name: 'Centro de Control', href: '/admin', icon: LayoutDashboard },
  { name: 'Usuarios', href: '/admin/users', icon: Users },
  { name: 'Publicaciones', href: '/admin/moderation', icon: Package },
  { name: 'Categorias', href: '/admin/categories', icon: FolderOpen },
  { name: 'Mensajes', href: '/admin/messages', icon: MessageSquare },
  { name: 'Soporte / Tickets', href: '/admin/support', icon: LifeBuoy, countKey: 'support' },
  { name: 'Suscripciones & IA', href: '/admin/subscriptions', icon: CreditCard },
  { name: 'Recepcionista IA', href: '/admin/ai', icon: Bot },
  { name: 'Subastas', href: '/admin/auctions', icon: Gavel, countKey: 'fraud' },
  { name: 'Enbixos', href: '/admin/enbixos', icon: Truck, countKey: 'enbixos' },
  { name: 'Morphs / Taxonomia', href: '/admin/morphs', icon: Dna, countKey: 'morphs' },
  { name: 'Compliance', href: '/admin/compliance', icon: Shield, countKey: 'compliance' },
];

export default function AdminLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [counts, setCounts] = useState({});

  const loadCounts = useCallback(async () => {
    try {
      const data = await api.adminNotificationCounts();
      setCounts(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (user && !['admin', 'super_admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadCounts();
    const interval = setInterval(loadCounts, 30000);
    return () => clearInterval(interval);
  }, [user, navigate, loadCounts]);

  if (!user || !['admin', 'super_admin'].includes(user.role)) return null;

  return (
    <div className="min-h-screen bg-[#060608]" data-testid="admin-layout">
      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-zinc-900 border border-white/5 text-zinc-400"
        data-testid="admin-sidebar-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-[#08080B] border-r border-white/[0.04] z-40 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Brand */}
        <div className="p-5 border-b border-white/[0.04]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-zinc-100 tracking-tight">MorphMX</p>
              <p className="text-[10px] text-red-400/80 uppercase tracking-widest font-medium">Control Center</p>
            </div>
          </div>
        </div>

        {/* User */}
        <div className="px-5 py-4 border-b border-white/[0.04]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 text-xs font-bold">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-zinc-200 truncate">{user.name}</p>
              <p className="text-[10px] text-zinc-600">{isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 flex-1 overflow-y-auto">
          <div className="space-y-0.5">
            {adminNav.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));
              const count = item.countKey ? (counts[item.countKey] || 0) : 0;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'bg-red-500/8 text-red-400 border border-red-500/10'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02]'
                  }`}
                  data-testid={`admin-nav-${item.href.split('/').pop()}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{item.name}</span>
                  {count > 0 && (
                    <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 animate-pulse" data-testid={`badge-${item.countKey}`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                  {isActive && !count && <ChevronRight className="w-3.5 h-3.5 opacity-50" />}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-white/[0.04]">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-600 hover:text-red-400 hover:bg-red-500/5 w-full transition-all"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
